import React, { useState } from "react";
import { Link, useLocation } from "wouter";
import { Bot, FlaskConical, Plus, Sparkles } from "lucide-react";
import { api, fmtMs, fmtUsd, invalidate, timeAgo, toastError, useQuery } from "../lib";
import { Badge, Button, Dot, Empty, Field, KindIcon, Modal, PassBar, Spark, cx } from "../ui";
import { NewTargetModal } from "./modals";
import { ExecutionDrawer, LogsTable } from "./logs";
import { FeedbackInbox } from "./inbox";

export function bestCell(summary: any[] | undefined) {
  if (!summary?.length) return null;
  return [...summary].sort((a, b) => b.passed / Math.max(1, b.done) - a.passed / Math.max(1, a.done))[0];
}

export function ProjectPage({ p, tab }: { p: string; tab: string }) {
  const { data: project, error } = useQuery<any>(`/api/projects/${p}`, { poll: 10_000 });
  const [newKind, setNewKind] = useState<"prompt" | "agent" | null>(null);
  const [openEx, setOpenEx] = useState<string | null>(null);
  const [, nav] = useLocation();
  if (error) return <div className="page">{error.message}</div>;
  const tabs = [
    { key: "overview", label: "Visão geral", href: `/p/${p}` },
    { key: "entrada", label: "Caixa de entrada", href: `/p/${p}/entrada`, count: project?.feedbackOpen },
    { key: "avaliacoes", label: "Avaliações", href: `/p/${p}/avaliacoes` },
    { key: "usage", label: "Usage", href: `/p/${p}/usage` },
  ];
  return (
    <>
      <div className="topbar">
        <div className="crumbs"><Link href="/">Projetos</Link><span>/</span><span className="cur">{project?.name ?? p}</span></div>
        <nav className="tabs">
          {tabs.map((t) => (
            <Link key={t.key} href={t.href} className={cx("tab", tab === t.key && "active")}>
              {t.label}
              {t.count ? <span className="count">{t.count}</span> : null}
            </Link>
          ))}
        </nav>
      </div>
      <div className="content">
        <div className="page">
          {tab === "overview" && project && (
            <>
              <div className="page-head">
                <div>
                  <h1 className="page-title">{project.name}</h1>
                  <div className="page-sub">{project.description}</div>
                </div>
                <div className="row">
                  <Button icon={<Bot size={14} />} onClick={() => setNewKind("agent")}>Novo agente</Button>
                  <Button variant="primary" icon={<Sparkles size={14} />} onClick={() => setNewKind("prompt")}>Novo prompt</Button>
                </div>
              </div>
              <div className="card">
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>Alvo</th><th>Produção</th><th>Staging</th><th>Chamadas 24 h</th><th className="r">p50</th><th className="r">Custo 24 h</th><th>Última avaliação</th></tr>
                    </thead>
                    <tbody>
                      {project.targets.map((t: any) => {
                        const best = bestCell(t.lastEval?.summary);
                        return (
                          <tr key={t.slug} className="click" onClick={() => nav(`/p/${p}/t/${t.slug}`)}>
                            <td>
                              <div className="row" style={{ gap: 11 }}>
                                <KindIcon kind={t.kind} size="lg" />
                                <div style={{ minWidth: 0 }}>
                                  <div className="row" style={{ gap: 8, fontWeight: 500 }}>
                                    {t.name}
                                    {project.feedbackByTarget?.[t.slug] ? <Badge tone="amber">{project.feedbackByTarget[t.slug]} feedback{project.feedbackByTarget[t.slug] > 1 ? "s" : ""}</Badge> : null}
                                  </div>
                                  <div className="faint ellipsis" style={{ fontSize: 12, maxWidth: 320 }}>{t.description || (t.kind === "agent" ? "agente" : "prompt")}</div>
                                </div>
                              </div>
                            </td>
                            <td>{t.channels?.production ? <span className="row mono" style={{ fontSize: 12 }}><Dot tone="green" />v{t.channels.production}</span> : <span className="faint">—</span>}</td>
                            <td>{t.channels?.staging ? <span className="row mono" style={{ fontSize: 12 }}><Dot tone="amber" />v{t.channels.staging}</span> : <span className="faint">—</span>}</td>
                            <td><div className="row" style={{ gap: 10 }}><Spark values={t.stats?.series ?? Array(24).fill(0)} width={70} height={20} /><span className="mono" style={{ fontSize: 12 }}>{t.stats?.requests ?? 0}</span></div></td>
                            <td className="r mono" style={{ fontSize: 12 }}>{fmtMs(t.stats?.p50)}</td>
                            <td className="r mono faint" style={{ fontSize: 12 }}>{t.stats ? fmtUsd(t.stats.costUsd) : "—"}</td>
                            <td>{best ? <PassBar passed={best.passed} total={best.done} /> : <span className="faint">—</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {!project.targets.length && (
                    <Empty icon={<Sparkles size={18} />} title="Nenhum prompt ou agente" action={<Button variant="primary" icon={<Plus size={14} />} onClick={() => setNewKind("prompt")}>Criar o primeiro</Button>}>
                      Um prompt é uma chamada de modelo; um agente roda num sandbox com um contexto de arquivos.
                    </Empty>
                  )}
                </div>
              </div>

              {project.evals.length > 0 && (
                <div className="section">
                  <div className="section-head"><h2 className="section-title">Avaliações recentes</h2><Link href={`/p/${p}/avaliacoes`} className="faint" style={{ fontSize: 12 }}>todas</Link></div>
                  <EvalList p={p} evals={project.evals.slice(0, 5)} names={Object.fromEntries(project.targets.map((t: any) => [t.slug, t.name]))} />
                </div>
              )}
            </>
          )}
          {tab === "avaliacoes" && <ProjectEvals p={p} />}
          {tab === "entrada" && <FeedbackInbox p={p} project={project} />}
          {(tab === "usage" || tab === "logs") && (
            <>
              <div className="page-head"><div><h1 className="page-title">Usage</h1><div className="page-sub">Execuções de todos os alvos de {project?.name}.</div></div></div>
              <LogsTable project={p} onOpen={setOpenEx} />
            </>
          )}
        </div>
      </div>
      <NewTargetModal open={!!newKind} kind={newKind ?? "prompt"} onClose={() => setNewKind(null)} project={p} />
      <ExecutionDrawer id={openEx} onClose={() => setOpenEx(null)} />
    </>
  );
}

function ProjectEvals({ p }: { p: string }) {
  const { data } = useQuery<any[]>(`/api/projects/${p}/evals`, { poll: 4000 });
  const { data: project } = useQuery<any>(`/api/projects/${p}`);
  const names = Object.fromEntries((project?.targets ?? []).map((t: any) => [t.slug, t.name]));
  return (
    <>
      <div className="page-head"><div><h1 className="page-title">Avaliações</h1><div className="page-sub">Cada avaliação roda versões e modelos contra os casos de teste do alvo e guarda o resultado de cada caso.</div></div></div>
      {data && <EvalList p={p} evals={data} showTarget names={names} />}
    </>
  );
}

export function EvalList({ p, evals, showTarget = true, names = {} }: { p: string; evals: any[]; showTarget?: boolean; names?: Record<string, string> }) {
  const [, nav] = useLocation();
  if (!evals.length) return <div className="card"><Empty icon={<FlaskConical size={18} />} title="Nenhuma avaliação ainda">Abra um prompt ou agente, vá em Avaliações, adicione casos de teste e rode.</Empty></div>;
  return (
    <div className="card">
      <table className="table">
        <thead><tr><th>Quando</th>{showTarget && <th>Alvo</th>}<th>Combinações</th><th>Melhor resultado</th><th>Status</th></tr></thead>
        <tbody>
          {evals.map((e) => {
            const best = bestCell(e.summary);
            return (
              <tr key={e.id} className="click" onClick={() => nav(`/p/${p}/e/${e.id}`)}>
                <td className="faint mono" style={{ fontSize: 12 }}>{timeAgo(e.createdAt)}</td>
                {showTarget && <td>{names[e.target] ?? e.target}</td>}
                <td>
                  <div className="row wrap" style={{ gap: 5 }}>
                    {(e.summary ?? []).map((s: any) => <Badge key={s.cell} tone="plain">{s.version} · {s.model.split("/").pop()}</Badge>)}
                  </div>
                </td>
                <td>{best ? <span className="row"><PassBar passed={best.passed} total={best.done} /><span className="faint mono" style={{ fontSize: 11 }}>{best.version}</span></span> : "—"}</td>
                <td>{e.status === "running" ? <span className="row faint"><span className="spinner" />{e.done}/{e.total}</span> : e.status === "error" ? <Badge tone="red">erro</Badge> : <Badge tone="green">pronto</Badge>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
